import React, { useEffect, useState } from "react";
import { Box, Button, VStack } from "@chakra-ui/react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { keymap } from "@codemirror/view";
import { defaultKeymap } from "@codemirror/commands";
import { AddIcon } from "@chakra-ui/icons";

interface IMarkDownProps {
  content: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  disabled?: boolean;
}

export const CodeEditor: React.FC<IMarkDownProps> = ({
  content,
  onChange,
  onBlur,
  disabled,
}) => {
  const [code, setCode] = useState(content || "");

  useEffect(() => {
    setCode(content || "");

    return () => setCode("");
  }, [content]);

  const handleChange = (value: string) => {
    onChange(value);
    setCode(value);
  };

  const handleSave = () => {
    onChange(code);
  };

  // Create a custom keymap for Ctrl/Cmd + S
  const customKeymap = keymap.of([
    {
      key: "Mod-s",
      run: () => {
        handleSave();
        return true;
      },
      preventDefault: true,
    },
  ]);

  return (
    <VStack width="100%" spacing={4} align="stretch">
      <Box position="relative">
        <Button
          leftIcon={<AddIcon />}
          onClick={handleSave}
          position="absolute"
          variant={"ghost"}
          top={2}
          right={2}
          size="sm"
          zIndex={2}
          colorScheme="blue"
          disabled={disabled}
        >
          Save (Ctrl+S)
        </Button>
        <Box minH="20rem" width="100%" borderRadius="md">
          <CodeMirror
            value={code}
            extensions={[
              javascript({ jsx: true, typescript: true }),
              customKeymap, // Custom keymap for save shortcut
              keymap.of(defaultKeymap),
            ]}
            onChange={handleChange}
            onBlur={() => onBlur && onBlur(code)}
            editable={!disabled}
            style={{
              fontSize: "14px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            theme={"dark"}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              highlightSelectionMatches: true,
            }}
          />
        </Box>
      </Box>
    </VStack>
  );
};

export default CodeEditor;
